import React, { useState, memo } from "react"
import styled from "styled-components"
import { Form } from "react-final-form"

import { PrimaryButton } from "components/buttons"
import { Flex, FlexColumn } from "components/box"
import { TextAreaField } from "components/form"
import { Paragraph } from "components/text"
import DoctorProfile from "pages/dashboard/treatment-plan/treatment-plan-types/components/doctor/profile"

import { useSendConsultationMessage } from "graphql/mutations/sendConsultationMessage"
import { GraphQLError } from "shared/apollo/graphql-error"
import { ConsultationFragmentConsultation, ConsultationMessageFromEnum } from "typings/types"

const Container = styled(Flex).attrs({
  flexDirection: ["column", "column", "row"]
})``

const ProfileContainer = styled(FlexColumn).attrs({
  alignSelf: ["center", "center", "flex-start"],
  p: [4, 4, 7, 7]
})`
  border-radius: 5px;
  background-color: ${props => props.theme.colors.ice};
`

const MessageContainer = styled(FlexColumn).attrs({
  ml: [0, 0, 5]
})`
  flex: 1 1 auto;
`

const MessageForm = styled.form`
  width: 100%;
`

const MessageField = styled(TextAreaField).attrs({
  mt: [5, 5, 0]
})`
  width: 100%;
  /* We can't pass input styles via props, so apply styles via tag nesting */
  textarea {
    min-height: 120px;
    resize: none;
  }
  @media screen and (min-width: ${props => props.theme.breakpoints.md}px) {
    textarea {
      min-height: 500px;
    }
  }
`

type MessageDoctorFormValues = {
  body: string
}

const MessageDoctor: React.FC<{ consultation: ConsultationFragmentConsultation }> = ({ consultation }) => {
  const [submited, setSubmited] = useState(false)
  const [sendConsultationMessage, { loading, error }] = useSendConsultationMessage()

  const onSubmit = async ({ body }: MessageDoctorFormValues) => {
    if (!body.trim()) {
      setSubmited(false)
      return
    }

    try {
      await sendConsultationMessage({
        variables: {
          from: ConsultationMessageFromEnum.User,
          consultationId: consultation.id,
          body
        }
      })

      setSubmited(true)
    } catch {
      setSubmited(false)
    }
  }

  if (!consultation.doctor) {
    return (
      <Paragraph textAlign="center" mt={6}>
        Doctor is not assigned yet
      </Paragraph>
    )
  }

  return (
    <Container>
      <ProfileContainer>
        <DoctorProfile doctor={consultation.doctor} />
      </ProfileContainer>
      <MessageContainer>
        <Form
          initialValues={{
            body: ""
          }}
          onSubmit={onSubmit}
          render={({ handleSubmit, submitError, form, valid }) => (
            <MessageForm
              onSubmit={async event => {
                await handleSubmit(event)

                if (valid) {
                  form.reset()
                }
              }}
            >
              <MessageField name="body" placeholder="Start typing..." required={!submited} disabled={loading} />
              <PrimaryButton type="submit" loading={loading} disabled={loading} sizing="lg" mt={5} mb={7}>
                Message doctor
              </PrimaryButton>
              {submitError && (
                <Paragraph color="salmon" mt={2}>
                  {submitError}
                </Paragraph>
              )}
              {error && <GraphQLError error={error} />}
            </MessageForm>
          )}
        />
      </MessageContainer>
    </Container>
  )
}

export default memo(MessageDoctor)
